/**
 * "Sync all sheets" button for the CashFlow page.
 * Fires sync for every tracked sheet registered in module=cashflow, then polls
 * all jobs concurrently and reports the aggregate result.
 */

import { useEffect, useRef, useState } from 'react';
import {
  postCashflowSync,
  fetchSyncJob,
  fetchTrackedSheets,
  type SyncJob,
  type SheetSyncJobRef,
} from '../../utils/api';

interface SyncButtonProps {
  onSyncComplete: () => Promise<void> | void;
  isAdmin: boolean;
}

type UIState =
  | { kind: 'idle' }
  | { kind: 'no_sheets' }
  | { kind: 'syncing'; total: number; done: number }
  | {
      kind: 'finished';
      at: Date;
      successCount: number;
      errorCount: number;
      total: number;
      written: number;
      errors: string[];
    };

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 90_000;

export default function SyncButton({ onSyncComplete, isAdmin }: SyncButtonProps) {
  const [state, setState] = useState<UIState>({ kind: 'idle' });
  const [sheetCount, setSheetCount] = useState<number | null>(null);
  const pollHandles = useRef<number[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sheets = await fetchTrackedSheets('cashflow');
        if (cancelled) return;
        setSheetCount(sheets.length);
        if (sheets.length === 0) setState({ kind: 'no_sheets' });
      } catch {
        if (!cancelled) setSheetCount(0);
      }
    })();
    return () => {
      cancelled = true;
      pollHandles.current.forEach((h) => window.clearTimeout(h));
      pollHandles.current = [];
    };
  }, []);

  async function waitForJob(jobId: string, deadline: number): Promise<SyncJob | null> {
    while (Date.now() < deadline) {
      try {
        const job = await fetchSyncJob(jobId);
        if (job.status !== 'pending' && job.status !== 'running') return job;
      } catch {
        return null;
      }
      await new Promise<void>((resolve) => {
        const h = window.setTimeout(() => resolve(), POLL_INTERVAL_MS);
        pollHandles.current.push(h);
      });
    }
    return null;
  }

  async function handleClick() {
    if (state.kind === 'syncing') return;
    setState({ kind: 'syncing', total: 0, done: 0 });

    let triggered: SheetSyncJobRef[] = [];
    try {
      const result = await postCashflowSync();
      triggered = result.jobs;
    } catch (err) {
      setState({
        kind: 'finished',
        at: new Date(),
        successCount: 0,
        errorCount: 1,
        total: 0,
        written: 0,
        errors: [err instanceof Error ? err.message : 'Could not trigger sync'],
      });
      return;
    }

    setState({ kind: 'syncing', total: triggered.length, done: 0 });

    // Already-failed jobs (webhook fire failed) count immediately.
    let successCount = 0;
    let errorCount = triggered.filter((j) => j.status === 'error').length;
    let totalWritten = 0;
    const allErrors: string[] = triggered.filter((j) => j.status === 'error').map(
      (j) => j.error ?? 'Webhook failed',
    );

    const runningJobs = triggered.filter((j) => j.status === 'running');
    const deadline = Date.now() + POLL_TIMEOUT_MS;

    let done = errorCount;
    const tick = () =>
      setState({ kind: 'syncing', total: triggered.length, done });

    await Promise.all(
      runningJobs.map(async (j) => {
        const final = await waitForJob(j.job_id, deadline);
        done++;
        tick();
        if (!final) {
          errorCount++;
          allErrors.push(`Job ${j.job_id.slice(0, 8)} timed out`);
          return;
        }
        if (final.status === 'success') {
          successCount++;
          const rp = final.response_payload as { written?: number } | null;
          if (rp && typeof rp.written === 'number') totalWritten += rp.written;
        } else {
          errorCount++;
          if (final.error_message) allErrors.push(final.error_message);
          if (final.validation_errors) allErrors.push(...final.validation_errors.slice(0, 3));
        }
      }),
    );

    setState({
      kind: 'finished',
      at: new Date(),
      successCount,
      errorCount,
      total: triggered.length,
      written: totalWritten,
      errors: allErrors,
    });

    await onSyncComplete();
  }

  const isSyncing = state.kind === 'syncing';
  const disabled = isSyncing || sheetCount === 0;

  let label = '🔄 Sync all sheets';
  if (isSyncing) {
    label = state.total > 0 ? `⏳ Syncing… ${state.done}/${state.total}` : '⏳ Starting…';
  }

  let hint: string | null = null;
  if (sheetCount === 0) {
    hint = isAdmin
      ? 'No sheets registered. Add one in Settings to enable sync.'
      : 'No sheets registered — ask an admin to add one in Settings.';
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
      <button
        onClick={handleClick}
        disabled={disabled}
        title={hint ?? undefined}
        style={{
          padding: '8px 14px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border)',
          background: disabled ? '#f4f4f4' : 'var(--color-accent)',
          color: disabled ? 'var(--color-text-secondary)' : '#fff',
          fontWeight: 700,
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: '0.875rem',
        }}
      >
        {label}
      </button>

      {state.kind === 'finished' && (
        <div style={{ maxWidth: 360 }}>
          <span
            style={{
              fontSize: '0.75rem',
              color: state.errorCount > 0 ? '#b91c1c' : 'var(--color-text-secondary)',
              fontWeight: state.errorCount > 0 ? 600 : 400,
            }}
          >
            {state.errorCount > 0 ? '⚠️' : '✓'} {state.successCount}/{state.total} succeeded
            {state.written > 0 && <> · {state.written} rows written</>}
            {' · '}
            {state.at.toLocaleTimeString()}
          </span>
          {state.errors.length > 0 && (
            <ul
              style={{
                fontSize: '0.7rem',
                color: '#b91c1c',
                margin: '4px 0 0 16px',
                padding: 0,
              }}
            >
              {state.errors.slice(0, 5).map((e, i) => (
                <li key={i}>{e}</li>
              ))}
              {state.errors.length > 5 && <li>…and {state.errors.length - 5} more</li>}
            </ul>
          )}
        </div>
      )}

      {state.kind !== 'finished' && hint && (
        <span style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)' }}>{hint}</span>
      )}
    </div>
  );
}
